import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { searchStyles } from "../../styles/formStyles";
import { IDeliveryAssignmentsListDTO } from "../../interfaces/Delivery";
import { getdeliveryAssignmentsList } from "../../services/Delivery";

const getAvailabilityStyle = (status: string) => {
  if (status === "available") return { bg: "rgba(52, 199, 89, 0.10)", color: "#00B221" };
  return { bg: "rgba(74, 85, 101, 0.10)", color: "#61708A" };
};

const DeliveryAssignment = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [assignments, setAssignments] = useState<Array<IDeliveryAssignmentsListDTO>>([]);
  const [loading, setLoading] = useState(false);
  const rowsPerPage = 9;

  const fetchAllAssignments = async (searchKeyword?: string) => {
    try {
      setLoading(true);
      const assignmentsRes = await getdeliveryAssignmentsList(searchKeyword || undefined);
      setAssignments(Array.isArray(assignmentsRes) ? assignmentsRes : []);
    } catch {
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchAllAssignments(value || undefined);
    }, 500);
  };
  useEffect(() => {
    fetchAllAssignments();
  }, []);

  const paginatedData = assignments.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const totalPages = Math.ceil(assignments.length / rowsPerPage);

  return (
    <>
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <TextField
            size="small"
            placeholder="Search by delivery partner, customer..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "grey.500" }} />
                </InputAdornment>
              ),
            }}
            sx={searchStyles}
          />
        </Box>

        <Box sx={{ p: 1, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Paper sx={{ maxHeight: "500px", overflow: "auto", "&::-webkit-scrollbar": { width: "8px", height: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Delivery Partner ID</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Delivery partner</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Assigned Pin codes</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Total Packages</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.875rem", bgcolor: "background.default", p: 1, whiteSpace: "nowrap" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                      <Typography color="grey.500">No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => (
                    <TableRow key={row.partnerId} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                      <TableCell align="center" sx={{ p: 1 }}>{row.partnerId}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>{row.name}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        {!row.assignedPincodes || row.assignedPincodes.length === 0 ? "--------" : (
                          <>
                            {row.assignedPincodes.slice(0, 2).join(", ")}
                            {row.assignedPincodes.length > 2 && (
                              <Tooltip title={row.assignedPincodes.slice(2).join(", ")} arrow>
                                <Typography component="span" sx={{ ml: 0.5, color: "primary.main", cursor: "pointer", fontSize: "inherit" }}>
                                  +{row.assignedPincodes.length - 2} more
                                </Typography>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>{row.totalPackages}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box sx={{ display: "inline-block", px: "0.7rem", py: "0.25rem", borderRadius: "6px", fontSize: "12px", fontWeight: 600, lineHeight: "16px", textAlign: "center", backgroundColor: getAvailabilityStyle(row.status).bg, color: getAvailabilityStyle(row.status).color, minWidth: "120px" }}>
                          {row.status === "available" ? "Available" : "Not Available"}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Paper>

      {assignments.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center"}}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </>
  );
};
export default DeliveryAssignment;
